/**
 * 🟥 CUADRO NEGRO 2026-02 — ReviewBox
 *
 * Renderiza una "revisión estructurada" devuelta por POST /api/review/chat:
 *   { type: "review", title, sections: [{name, content}], rawText }
 *
 * Diseño minimalista alineado al chat actual:
 *   - Marco simple (border + padding) que se distingue de un mensaje normal.
 *   - Título en la cabecera + icono ClipboardCheck.
 *   - 5 secciones con nombre destacado y contenido en preserve-whitespace.
 *   - Botón para copiar todo el rawText al portapapeles.
 *   - Botón opcional "Convertir a módulo" que llama a onConvertToModule()
 *     (lo provee MessageBubble usando el endpoint /api/workspace/auto).
 */
import { useState } from 'react';
import { ClipboardCheck, Copy, Check, FolderInput, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

export const ReviewBox = ({ review, onConvertToModule }) => {
  const { language } = useLanguage();
  const [copied, setCopied] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [converted, setConverted] = useState(false);

  if (!review || !Array.isArray(review.sections) || review.sections.length === 0) {
    return null;
  }

  const isEs = (language || 'es').toLowerCase().startsWith('es');
  const title = review.title || (isEs ? 'Revisión del proyecto' : 'Project review');

  const handleCopy = async () => {
    try {
      const text = review.rawText || '';
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success(isEs ? 'Revisión copiada' : 'Review copied');
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error(isEs ? 'No se pudo copiar' : 'Could not copy');
    }
  };

  const handleConvert = async () => {
    if (!onConvertToModule || isConverting) return;
    setIsConverting(true);
    try {
      const ok = await onConvertToModule({
        title,
        rawText: review.rawText || '',
      });
      if (ok) {
        setConverted(true);
        setTimeout(() => setConverted(false), 3000);
      }
    } finally {
      setIsConverting(false);
    }
  };

  return (
    <div
      className="mt-1 rounded-xl border border-indigo-500/40 bg-indigo-950/30 light:bg-indigo-50 light:border-indigo-200 p-4 sm:p-5"
      data-testid="review-box"
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-indigo-400 light:text-indigo-600" />
          <h4 className="text-sm font-semibold text-indigo-300 light:text-indigo-800" data-testid="review-title">
            {title}
          </h4>
        </div>
        <div className="flex items-center gap-1">
          {onConvertToModule && (
            <button
              onClick={handleConvert}
              disabled={isConverting}
              className="flex items-center gap-1 px-2 py-1 text-xs rounded-md text-zinc-400 hover:text-emerald-300 hover:bg-emerald-500/10 light:text-zinc-500 light:hover:text-emerald-700 light:hover:bg-emerald-100 transition-colors disabled:opacity-60"
              title={isEs ? 'Convertir esta revisión en un módulo editable del Área de Trabajo Interna' : 'Convert this review into an editable module in the Internal Workspace'}
              data-testid="review-convert-btn"
            >
              {isConverting ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : converted ? (
                <Check className="w-3 h-3 text-emerald-400" />
              ) : (
                <FolderInput className="w-3 h-3" />
              )}
              <span>
                {isConverting
                  ? (isEs ? 'Enviando…' : 'Sending…')
                  : converted
                    ? (isEs ? 'Enviado' : 'Sent')
                    : (isEs ? 'Convertir a módulo' : 'Convert to module')}
              </span>
            </button>
          )}
          <button
            onClick={handleCopy}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded-md text-zinc-400 hover:text-indigo-300 hover:bg-indigo-500/10 light:text-zinc-500 light:hover:text-indigo-700 light:hover:bg-indigo-100 transition-colors"
            title={isEs ? 'Copiar revisión completa' : 'Copy full review'}
            data-testid="review-copy-btn"
          >
            {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
            <span>{copied ? (isEs ? 'Copiado' : 'Copied') : (isEs ? 'Copiar' : 'Copy')}</span>
          </button>
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-3">
        {review.sections.map((section, idx) => (
          <div
            key={`${section.name}-${idx}`}
            className="border-l-2 border-indigo-500/40 pl-3"
            data-testid={`review-section-${idx}`}
          >
            <div className="text-xs font-semibold uppercase tracking-wide text-indigo-400 light:text-indigo-700 mb-1">
              {section.name}
            </div>
            <div className="text-sm text-zinc-200 light:text-zinc-800 whitespace-pre-wrap leading-relaxed">
              {section.content || '—'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReviewBox;
